import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Shell from "../components/Shell";
import { api, eventTypeLabel, daysUntil } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Plus, ArrowUpRight, CalendarDays, Search, ArrowUpDown, MoreVertical,
  Archive, Trash2, RotateCcw, X, CheckSquare, Square,
} from "lucide-react";
import ApplyTemplateDialog from "../components/ApplyTemplateDialog";
import EventTypePicker from "../components/EventTypePicker";
import RecurrenceControl from "../components/RecurrenceControl";
import EventActionsMenu from "../components/EventActionsMenu";

const FILTERS = [
  { id: "all",         label: "All" },
  { id: "today",       label: "Today" },
  { id: "this-week",   label: "This Week" },
  { id: "upcoming",    label: "Upcoming" },
  { id: "past",        label: "Past" },
  { id: "overdue",     label: "Overdue" },
  { id: "unconfirmed", label: "Unconfirmed" },
  { id: "unpaid",      label: "Unpaid" },
  { id: "archived",    label: "Archived" },
];

const SORTS = [
  { id: "date",       label: "Event date (soonest first)" },
  { id: "recent",     label: "Recently updated" },
  { id: "readiness",  label: "Readiness (lowest first)" },
  { id: "alphabet",   label: "Alphabetical" },
];

/**
 * Dedicated Events workspace — a focused list of every event the user
 * has access to, separate from the Command Center dashboard. Includes
 * search, quick filters (URL-driven so pulse-bar chips can deep-link
 * in), sort options, and the new-event dialog.
 */
export default function EventsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const filter = searchParams.get("filter") || "all";
  const sort = searchParams.get("sort") || "date";
  const setFilter = (id) => setSearchParams((p) => {
    const next = new URLSearchParams(p);
    if (id === "all") next.delete("filter"); else next.set("filter", id);
    return next;
  });
  const setSort = (id) => setSearchParams((p) => {
    const next = new URLSearchParams(p);
    if (id === "date") next.delete("sort"); else next.set("sort", id);
    return next;
  });
  const [open, setOpen] = useState(false);
  const [templateFor, setTemplateFor] = useState(null);
  const [form, setForm] = useState({
    title: "", event_type: "wedding", event_date: "", venue: "",
    couple_names: "", description: "", cover_image: "", client_id: "",
    is_recurring: false, recurrence: null,
  });

  const [archivedEvents, setArchivedEvents] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Reuse the /dashboard endpoint since it already ships events with
      // readiness scores + counts (which the cards render).
      const [dashRes, clRes, archivedRes] = await Promise.all([
        api.get("/dashboard"),
        api.get("/clients").catch(() => ({ data: [] })),
        api.get("/events", { params: { include_archived: true } }).catch(() => ({ data: [] })),
      ]);
      setEvents(dashRes.data.events || []);
      setClients(clRes.data || []);
      // Only the archived ones — the live list already has non-archived from /dashboard.
      setArchivedEvents((archivedRes.data || []).filter((e) => e.archived_at));
    } catch (e) {
      toast.error("Could not load events");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title || !form.event_date) { toast.error("Please add a title and date"); return; }
    try {
      const { data } = await api.post("/events", { ...form, client_id: form.client_id || null });
      toast.success("Event created");
      setOpen(false);
      setTemplateFor(data);
    } catch { toast.error("Could not create event"); }
  };

  const filtered = useMemo(() => {
    const source = filter === "archived" ? archivedEvents : events;
    const lc = q.trim().toLowerCase();
    const arr = source
      .filter((e) => {
        if (!lc) return true;
        return (e.title || "").toLowerCase().includes(lc)
            || (e.venue || "").toLowerCase().includes(lc)
            || (e.couple_names || "").toLowerCase().includes(lc)
            || eventTypeLabel(e.event_type).toLowerCase().includes(lc);
      })
      .filter((e) => {
        const d = daysUntil(e.event_date);
        const counts = e.readiness?.counts || {};
        if (filter === "today")       return d === 0;
        if (filter === "this-week")   return d >= 0 && d <= 7;
        if (filter === "upcoming")    return d >= 0;
        if (filter === "past")        return d < 0;
        if (filter === "overdue")     return (counts.tasks_overdue || 0) > 0;
        if (filter === "unconfirmed") return (counts.vendors_unconfirmed || 0) > 0;
        if (filter === "unpaid")      return (counts.budget_unpaid || 0) > 0;
        return true;
      });
    // sort
    if (sort === "alphabet") arr.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    else if (sort === "readiness") arr.sort((a, b) => (a.readiness?.score || 0) - (b.readiness?.score || 0));
    else if (sort === "recent") arr.sort((a, b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || ""));
    else arr.sort((a, b) => (a.event_date || "").localeCompare(b.event_date || ""));
    return arr;
  }, [events, archivedEvents, q, filter, sort]);

  const counts = useMemo(() => ({
    all:         events.length,
    today:       events.filter((e) => daysUntil(e.event_date) === 0).length,
    "this-week": events.filter((e) => { const d = daysUntil(e.event_date); return d >= 0 && d <= 7; }).length,
    upcoming:    events.filter((e) => daysUntil(e.event_date) >= 0).length,
    past:        events.filter((e) => daysUntil(e.event_date) < 0).length,
    overdue:     events.filter((e) => (e.readiness?.counts?.tasks_overdue || 0) > 0).length,
    unconfirmed: events.filter((e) => (e.readiness?.counts?.vendors_unconfirmed || 0) > 0).length,
    unpaid:      events.filter((e) => (e.readiness?.counts?.budget_unpaid || 0) > 0).length,
    archived:    archivedEvents.length,
  }), [events, archivedEvents]);

  // --- Multi-select (bulk archive / delete) ----------------------------
  // Owner-only checkboxes appear on each card. Selection state is scoped to
  // whatever filter is currently active — switching filters clears it so we
  // don't accidentally archive off-screen events.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  useEffect(() => { setSelectedIds(new Set()); }, [filter, q]);

  const ownedFiltered = useMemo(
    () => filtered.filter((e) => (e.role || "owner") === "owner"),
    [filtered],
  );
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const selectAllVisible = () => setSelectedIds(new Set(ownedFiltered.map((e) => e.id)));
  const clearSelection = () => setSelectedIds(new Set());
  const allVisibleSelected =
    ownedFiltered.length > 0 && ownedFiltered.every((e) => selectedIds.has(e.id));

  const bulkArchive = async () => {
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(
      ids.map((id) => api.post(`/events/${id}/archive`)),
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    if (ok) toast.success(`Archived ${ok} event${ok === 1 ? "" : "s"}.`);
    if (failed) toast.error(`${failed} event${failed === 1 ? "" : "s"} couldn't be archived.`);
    clearSelection();
    setBulkBusy(false);
    load();
  };
  const bulkUnarchive = async () => {
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    await Promise.allSettled(ids.map((id) => api.post(`/events/${id}/unarchive`)));
    toast.success(`Restored ${ids.length} event${ids.length === 1 ? "" : "s"}.`);
    clearSelection();
    setBulkBusy(false);
    load();
  };
  const bulkDelete = async () => {
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(ids.map((id) => api.delete(`/events/${id}`)));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    if (ok) toast.success(`Deleted ${ok} event${ok === 1 ? "" : "s"} and all associated data.`);
    if (failed) toast.error(`${failed} event${failed === 1 ? "" : "s"} couldn't be deleted.`);
    clearSelection();
    setConfirmBulkDelete(false);
    setBulkBusy(false);
    load();
  };

  return (
    <Shell>
      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-6 flex-wrap">
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2">
              WORKSPACE / EVENTS
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-black text-app tracking-tight leading-none">
              Events
            </h1>
            <p className="mt-2 text-sm text-muted">
              {counts.all === 0 ? "No events yet."
                : `${counts.all} total · ${counts.upcoming} upcoming · ${counts.today} today`}
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-event-btn" className="rounded-lg bevel-btn-primary h-10 px-5 font-medium">
                <Plus size={15} strokeWidth={2} className="mr-2" /> New event
              </Button>
            </DialogTrigger>
            <NewEventDialog form={form} setForm={setForm} clients={clients} create={create} onCancel={() => setOpen(false)} />
          </Dialog>
        </div>

        {/* Search + filter row */}
        {events.length > 0 && (
          <div className="mb-4 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[240px]">
                <Search size={13} strokeWidth={1.75} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-faint" />
                <Input
                  data-testid="events-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by title, venue, host, or type…"
                  className="rounded-sm pl-8 h-10 bg-surface border-hair"
                />
              </div>
              <div className="inline-flex items-center gap-1.5 bevel border-hair h-10 px-2 rounded-sm">
                <ArrowUpDown size={13} strokeWidth={1.75} className="text-faint" />
                <span className="font-mono text-[10px] tracking-widest uppercase text-faint">Sort</span>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger data-testid="events-sort-select" className="border-0 bg-transparent h-7 rounded-sm text-app font-mono text-[11px] uppercase tracking-wider min-w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SORTS.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`events-sort-option-${s.id}`}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="inline-flex flex-wrap bevel border-hair rounded-sm overflow-hidden">
              {FILTERS.filter((f) => f.id === "all" || (counts[f.id] || 0) > 0).map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  data-testid={`events-filter-${f.id}`}
                  className={`font-mono text-[10px] tracking-[0.2em] uppercase px-3 h-10 border-r border-hair last:border-r-0 ${
                    filter === f.id ? "bevel-btn-primary text-white" : "text-app hover:bg-inset"
                  }`}
                >
                  {f.label} <span className="font-normal opacity-70">[{counts[f.id] || 0}]</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="font-mono text-xs text-muted">LOADING…</div>
        ) : events.length === 0 ? (
          <EmptyState onCreate={() => setOpen(true)} />
        ) : filtered.length === 0 ? (
          <div className="bevel border-hair rounded-sm p-12 text-center">
            <div className="font-serif text-xl text-app">No matches.</div>
            <p className="text-sm text-muted mt-1">Try clearing the search or picking a different filter.</p>
          </div>
        ) : (
          <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3" data-testid="events-grid">
            {filtered.map((e) => (
              <EventCard
                key={e.id}
                ev={e}
                clients={clients}
                onChanged={load}
                selected={selectedIds.has(e.id)}
                onToggleSelect={() => toggleSelect(e.id)}
                selectionActive={selectedIds.size > 0}
              />
            ))}
          </div>
        )}
      </div>

      {selectedIds.size > 0 && (
        <BulkActionBar
          selectedCount={selectedIds.size}
          allVisibleSelected={allVisibleSelected}
          onSelectAll={selectAllVisible}
          onClear={clearSelection}
          onArchive={bulkArchive}
          onUnarchive={bulkUnarchive}
          onDelete={() => setConfirmBulkDelete(true)}
          busy={bulkBusy}
          isArchivedView={filter === "archived"}
        />
      )}

      <Dialog open={confirmBulkDelete} onOpenChange={(v) => !v && setConfirmBulkDelete(false)}>
        <DialogContent className="max-w-md bevel-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-app flex items-center gap-2">
              <Trash2 size={18} className="text-[#DC2626]" /> Delete {selectedIds.size} event{selectedIds.size === 1 ? "" : "s"}?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted leading-relaxed py-2">
            This permanently removes <strong className="text-app">{selectedIds.size} event{selectedIds.size === 1 ? "" : "s"}</strong>{" "}
            and every associated record (guests, vendors, budget, tasks, timeline, documents, chat threads,
            floor plans). This action <strong className="text-app">cannot be undone</strong>.
          </div>
          <div className="text-xs text-muted border border-hair bg-inset rounded-sm p-3 leading-relaxed">
            If you might want any of these back, cancel and use <strong className="text-app">Bulk archive</strong> instead.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmBulkDelete(false)} data-testid="bulk-delete-cancel">
              Cancel
            </Button>
            <Button
              onClick={bulkDelete}
              disabled={bulkBusy}
              data-testid="bulk-delete-confirm"
              className="rounded-sm bg-[#DC2626] hover:bg-[#B91C1C] text-white"
            >
              <Trash2 size={13} strokeWidth={2} className="mr-1.5" />
              {bulkBusy ? "Deleting…" : `Delete ${selectedIds.size} event${selectedIds.size === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ApplyTemplateDialog
        eventId={templateFor?.id}
        eventType={templateFor?.event_type}
        open={!!templateFor}
        onOpenChange={(v) => {
          if (!v && templateFor) { navigate(`/events/${templateFor.id}`); setTemplateFor(null); }
        }}
        onApplied={() => templateFor && navigate(`/events/${templateFor.id}`)}
      />
    </Shell>
  );
}

/* ---------- Event card (same treatment as the Command Center version) ---------- */
function EventCard({ ev, clients = [], onChanged, selected, onToggleSelect, selectionActive }) {
  const days = daysUntil(ev.event_date);
  const client = ev.client_id ? clients.find((c) => c.id === ev.client_id) : null;
  const initials = (ev.title || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const isToday = days === 0;
  const soon = days >= 0 && days <= 7;
  const past = days < 0;
  const isArchived = !!ev.archived_at;
  const isOwner = (ev.role || "owner") === "owner";
  const r = ev.readiness || {};
  const c = r.counts || {};

  const handleCheckbox = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleSelect?.();
  };

  return (
    <Link
      to={`/events/${ev.id}`}
      data-testid={`event-card-${ev.id}`}
      className={`group block bevel border-hair card-lift relative ${past ? "opacity-60" : ""} ${isArchived ? "opacity-70" : ""} ${selected ? "ring-2 ring-app" : ""}`}
    >
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${isArchived ? "bg-[#71717A]" : isToday ? "bg-[#EF4444]" : soon ? "bg-[#F59E0B]" : past ? "bg-[#E4E4E7]" : "bg-app"}`} />
      <div className="p-4 pl-5">
        <div className="flex items-center justify-between mb-3 font-mono text-[10px] tracking-widest uppercase">
          <div className="flex items-center gap-2">
            {isOwner && (
              <button
                onClick={handleCheckbox}
                data-testid={`event-select-${ev.id}`}
                className={`shrink-0 transition-opacity ${selectionActive || selected ? "opacity-100" : "opacity-40 group-hover:opacity-100"}`}
                aria-label={selected ? "Deselect event" : "Select event"}
              >
                {selected
                  ? <CheckSquare size={14} strokeWidth={2} className="text-app" />
                  : <Square size={14} strokeWidth={2} className="text-muted hover:text-app" />}
              </button>
            )}
            <span className="text-muted">
              EVT.{ev.id.slice(-6).toUpperCase()}
              {isArchived && <span className="ml-2 px-1.5 py-0.5 border border-hair rounded text-faint">ARCHIVED</span>}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {ev.role && ev.role !== "owner" && (
              <span className="bevel-btn-primary text-white px-1.5 py-0.5">{ev.role}</span>
            )}
            <EventActionsMenu ev={ev} onChanged={onChanged} />
            <ArrowUpRight size={13} strokeWidth={2} className="text-muted group-hover:text-app" />
          </div>
        </div>
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 bevel-btn-primary text-white flex items-center justify-center font-display font-black text-sm flex-shrink-0">
            {initials || "—"}
          </div>
          <div className="min-w-0 flex-1">
            {client && <div className="font-mono text-[10px] uppercase tracking-widest text-muted truncate">{client.name}</div>}
            <div className="font-display text-base font-bold text-app truncate leading-tight">{ev.title}</div>
            {ev.couple_names && <div className="text-xs text-muted truncate">{ev.couple_names}</div>}
          </div>
        </div>
        <div className="border-t border-hair pt-3 flex items-center justify-between text-xs mb-3">
          <span className="flex items-center gap-1.5 text-app">
            <CalendarDays size={12} strokeWidth={1.75} className="text-muted" />
            {new Date(ev.event_date).toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "2-digit" }).toUpperCase()}
          </span>
          <span className={`font-mono tabular-nums ${isToday ? "text-[#EF4444] font-semibold" : soon ? "text-[#F59E0B] font-semibold" : "text-app"}`}>
            {days === 0 ? "TODAY" : days < 0 ? `+${Math.abs(days)}D` : `${days}D`}
          </span>
        </div>
        <div className="grid grid-cols-4 gap-1 mb-2">
          <ReadinessCell label="T" value={c.tasks_total ? `${c.tasks_done}/${c.tasks_total}` : "—"} />
          <ReadinessCell label="V" value={c.vendors_total ? `${c.vendors_confirmed}/${c.vendors_total}` : "—"} />
          <ReadinessCell label="G" value={c.guests_total ? `${c.guests_responded}/${c.guests_total}` : "—"} />
          <ReadinessCell label="B" value={r.budget_pct != null ? `${r.budget_pct}%` : "—"} />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1 bg-surface-2">
            <div
              className={`h-full ${
                r.status === "critical" ? "bg-[#EF4444]"
                : r.status === "in-progress" ? "bg-[#F59E0B]"
                : r.status === "on-track" ? "bg-[#059669]"
                : "bg-app"
              }`}
              style={{ width: `${r.score || 0}%` }}
            />
          </div>
          <span className="font-mono text-xs tabular-nums text-app font-semibold w-9 text-right">{r.score || 0}%</span>
        </div>
      </div>
    </Link>
  );
}

function ReadinessCell({ label, value }) {
  return (
    <div className="text-center border border-hair px-1 py-1">
      <div className="font-mono text-[9px] text-faint">{label}</div>
      <div className="font-mono text-[10px] tabular-nums text-app">{value}</div>
    </div>
  );
}

/* ---------- Bulk action bar ---------- */
// Floating footer that appears when at least one event is selected. Provides
// bulk archive/unarchive/delete + a "select all visible" convenience. Only
// owned events (role === "owner") are selectable so we don't accidentally
// call archive on shared/vendor-scope events (backend would 403 anyway).
function BulkActionBar({
  selectedCount, allVisibleSelected, onSelectAll, onClear, onArchive, onUnarchive, onDelete,
  busy, isArchivedView,
}) {
  return (
    <div
      className="fixed inset-x-0 bottom-4 z-40 flex justify-center px-4 pointer-events-none"
      data-testid="bulk-action-bar"
    >
      <div className="pointer-events-auto bevel-lg bg-surface border border-hair shadow-xl px-4 py-3 flex items-center gap-3 max-w-4xl w-full sm:w-auto">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted">SELECTED</span>
          <span className="font-mono text-sm tabular-nums font-semibold text-app" data-testid="bulk-selection-count">
            {selectedCount}
          </span>
        </div>
        <div className="h-6 w-px bg-hair" />
        <button
          onClick={onSelectAll}
          disabled={allVisibleSelected}
          className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted hover:text-app disabled:opacity-40 disabled:pointer-events-none"
          data-testid="bulk-select-all"
        >
          Select all visible
        </button>
        <button
          onClick={onClear}
          className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted hover:text-app inline-flex items-center gap-1"
          data-testid="bulk-clear"
        >
          <X size={11} strokeWidth={2} /> Clear
        </button>
        <div className="h-6 w-px bg-hair ml-auto" />
        {isArchivedView ? (
          <button
            onClick={onUnarchive}
            disabled={busy}
            className="inline-flex items-center h-9 px-3 border border-hair hover:border-[#0A0A0A] font-mono text-[10px] tracking-[0.2em] uppercase text-app disabled:opacity-50"
            data-testid="bulk-unarchive"
          >
            <RotateCcw size={12} strokeWidth={2} className="mr-1.5" /> Restore
          </button>
        ) : (
          <button
            onClick={onArchive}
            disabled={busy}
            className="inline-flex items-center h-9 px-3 border border-hair hover:border-[#0A0A0A] font-mono text-[10px] tracking-[0.2em] uppercase text-app disabled:opacity-50"
            data-testid="bulk-archive"
          >
            <Archive size={12} strokeWidth={2} className="mr-1.5" /> Archive
          </button>
        )}
        <button
          onClick={onDelete}
          disabled={busy}
          className="inline-flex items-center h-9 px-3 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-mono text-[10px] tracking-[0.2em] uppercase disabled:opacity-50"
          data-testid="bulk-delete"
        >
          <Trash2 size={12} strokeWidth={2} className="mr-1.5" /> Delete
        </button>
      </div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="bevel border-hair p-12 text-center" data-testid="events-empty-state">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-3">EVENT INDEX / EMPTY</div>
      <div className="font-display text-2xl font-black text-app mb-2 tracking-tight">No events yet.</div>
      <p className="text-sm text-muted max-w-md mx-auto mb-6">
        Create your first event and the Command Center will start surfacing what needs your attention.
      </p>
      <Button onClick={onCreate} className="rounded-sm bg-[#0A0A0A] hover:bg-black h-10 px-5 font-medium">
        <Plus size={15} strokeWidth={2} className="mr-2" /> Create first event
      </Button>
    </div>
  );
}

function NewEventDialog({ form, setForm, clients, create, onCancel }) {
  return (
    <DialogContent className="max-w-lg bevel-lg">
      <DialogHeader>
        <DialogTitle className="font-serif text-2xl text-app">New event</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <Field label="Event title">
          <Input data-testid="event-title-input" placeholder="e.g. Alex & Jordan" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <EventTypePicker value={form.event_type} onChange={(v) => setForm({ ...form, event_type: v })} testId="event-type-select" />
          </Field>
          <Field label="Date">
            <Input data-testid="event-date-input" type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
          </Field>
        </div>
        <Field label="Venue (optional)">
          <Input data-testid="event-venue-input" placeholder="e.g. The Old Mill, Vermont" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} />
        </Field>
        <Field label="Host / Client name(s) (optional)">
          <Input placeholder="e.g. Alex & Jordan · ACME Co." value={form.couple_names} onChange={(e) => setForm({ ...form, couple_names: e.target.value })} />
        </Field>
        {clients.length > 0 && (
          <Field label="Attach to a client (optional)">
            <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="event-client-select" className="rounded-sm"><SelectValue placeholder="No client" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No client</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        )}
        <Field label="Description (optional)">
          <Textarea rows={2} className="rounded-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <RecurrenceControl value={{ is_recurring: form.is_recurring, recurrence: form.recurrence }} onChange={(v) => setForm({ ...form, ...v })} />
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel} data-testid="event-cancel-btn">Cancel</Button>
        <Button data-testid="event-create-submit" onClick={create} className="rounded-lg bevel-btn-primary">Create event</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="eyebrow block mb-2">{label}</Label>
      {children}
    </div>
  );
}
